import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, Send, MessageCircle, ChevronRight } from "lucide-react";
import { subscribe, postInquiry, whatsappLink, COMPANY } from "@/lib/api";
import { Reveal } from "@/components/Reveal";

export function Newsletter() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      const r = await subscribe(email);
      toast.success(r.status === "already_subscribed" ? "You're already subscribed." : "Subscribed — welcome aboard.");
      setEmail("");
    } catch { toast.error("Please enter a valid email."); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} data-testid="newsletter-form">
      <div className="eyebrow text-[hsl(var(--brass-2))] mb-3">Market Newsletter</div>
      <div className="flex border border-white/20 bg-white/5">
        <input
          data-testid="newsletter-email" type="email" required value={email}
          onChange={(e) => setEmail(e.target.value)} placeholder="Your email address"
          className="flex-1 bg-transparent px-3 py-2.5 text-sm text-white placeholder:text-white/40 outline-none"
        />
        <button data-testid="newsletter-submit" disabled={busy} className="px-4 bg-[hsl(var(--brass))] text-[hsl(var(--ink))] hover:bg-[hsl(var(--brass-2))] transition-colors">
          <Send size={16} />
        </button>
      </div>
    </form>
  );
}

export function SectionHeading({ eyebrow, title, sub, light, center }) {
  return (
    <div className={`${center ? "text-center mx-auto" : ""} max-w-3xl`}>
      {eyebrow && <div className={`eyebrow mb-4 ${light ? "text-[hsl(var(--brass-2))]" : "text-[hsl(var(--accent))]"}`}>{eyebrow}</div>}
      <h2 className={`font-display text-4xl sm:text-5xl leading-[1.05] tracking-tight ${light ? "text-[hsl(var(--paper))]" : "text-[hsl(var(--navy))]"}`}>{title}</h2>
      {sub && <p className={`mt-5 text-base leading-relaxed ${light ? "text-white/70" : "text-[hsl(var(--muted-foreground))]"}`}>{sub}</p>}
    </div>
  );
}

export function Breadcrumbs({ items }) {
  return (
    <nav data-testid="breadcrumbs" className="flex items-center gap-1.5 text-xs text-white/60 flex-wrap">
      {items.map((it, i) => (
        <span key={i} className="flex items-center gap-1.5">
          {it.to ? <Link to={it.to} className="hover:text-[hsl(var(--brass-2))]">{it.label}</Link> : <span className="text-[hsl(var(--brass-2))]">{it.label}</span>}
          {i < items.length - 1 && <ChevronRight size={12} />}
        </span>
      ))}
    </nav>
  );
}

export function Marquee({ items }) {
  const row = [...items, ...items];
  return (
    <div className="marquee py-8 border-y border-[hsl(var(--border))] bg-[hsl(var(--secondary))]">
      <div className="marquee__track">
        {row.map((t, i) => (
          <span key={i} className="font-display text-2xl md:text-3xl text-[hsl(var(--navy))] flex items-center gap-8 whitespace-nowrap">
            {t} <span className="text-[hsl(var(--brass))]">✦</span>
          </span>
        ))}
      </div>
    </div>
  );
}

export function CTASection() {
  return (
    <section className="bg-[hsl(var(--navy))] relative grain overflow-hidden">
      <div className="hero-grid absolute inset-0 opacity-40" />
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-24 relative">
        <Reveal>
          <div className="eyebrow text-[hsl(var(--brass-2))]">Ready to trade</div>
          <h2 className="font-display text-4xl sm:text-6xl text-[hsl(var(--paper))] max-w-3xl mt-4 leading-[1.03]">Let{"\u2019"}s move your next shipment with certainty.</h2>
          <p className="text-white/70 max-w-xl mt-5">Tell us the product, destination and volume. Our export desk responds within one business day with pricing, availability and lead times.</p>
          <div className="mt-9 flex flex-wrap gap-4">
            <Link to="/contact" data-testid="cta-quote" className="inline-flex items-center gap-2 bg-[hsl(var(--brass))] text-[hsl(var(--ink))] px-7 py-3.5 font-semibold hover:bg-[hsl(var(--brass-2))] transition-colors">
              Request a Quotation <ArrowUpRight size={18} />
            </Link>
            <a href={whatsappLink()} target="_blank" rel="noreferrer" data-testid="cta-whatsapp" className="inline-flex items-center gap-2 border border-white/25 text-[hsl(var(--paper))] px-7 py-3.5 font-semibold hover:bg-white/10 transition-colors">
              <MessageCircle size={18} /> WhatsApp
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export function InquiryForm({ product = "", compact }) {
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", country: "", product, quantity: "", incoterm: "", message: "", website: "" });
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await postInquiry({ ...form, type: product ? "quote" : "inquiry" });
      toast.success("Inquiry sent. Our export desk will reply within one business day.");
      setForm({ name: "", email: "", company: "", phone: "", country: "", product, quantity: "", incoterm: "", message: "", website: "" });
    } catch { toast.error("Something went wrong. Please try again."); }
    setBusy(false);
  };
  const inp = "w-full bg-white border border-[hsl(var(--border))] px-3.5 py-2.5 text-sm outline-none focus:border-[hsl(var(--brass))] transition-colors";
  return (
    <form onSubmit={submit} data-testid="inquiry-form" className="grid sm:grid-cols-2 gap-3">
      <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={upd("website")} name="website"
        style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} aria-hidden="true" />
      <input required placeholder="Full name *" value={form.name} onChange={upd("name")} className={inp} data-testid="inq-name" />
      <input required type="email" placeholder="Email *" value={form.email} onChange={upd("email")} className={inp} data-testid="inq-email" />
      <input placeholder="Company" value={form.company} onChange={upd("company")} className={inp} data-testid="inq-company" />
      <input placeholder="Phone / WhatsApp" value={form.phone} onChange={upd("phone")} className={inp} data-testid="inq-phone" />
      <input placeholder="Destination country" value={form.country} onChange={upd("country")} className={inp} data-testid="inq-country" />
      <input placeholder="Product" value={form.product} onChange={upd("product")} className={inp} data-testid="inq-product" />
      <input placeholder="Quantity (e.g. 2 x 40'FCL)" value={form.quantity} onChange={upd("quantity")} className={inp} data-testid="inq-quantity" />
      <select value={form.incoterm} onChange={upd("incoterm")} className={inp} data-testid="inq-incoterm">
        <option value="">Incoterm (optional)</option>
        {["EXW","FOB","CIF","CFR","DDP"].map((i) => <option key={i} value={i}>{i}</option>)}
      </select>
      <textarea required placeholder="Your message *" rows={compact ? 3 : 4} value={form.message} onChange={upd("message")} className={`${inp} sm:col-span-2`} data-testid="inq-message" />
      <button disabled={busy} data-testid="inq-submit" className="sm:col-span-2 inline-flex items-center justify-center gap-2 bg-[hsl(var(--navy))] text-[hsl(var(--paper))] px-6 py-3.5 font-semibold hover:bg-[hsl(var(--navy-2))] transition-colors disabled:opacity-60">
        {busy ? "Sending..." : "Send Inquiry"} <ArrowUpRight size={17} />
      </button>
    </form>
  );
}
